/**
 * PitchBlackTheme — atmospheric overlay for the Five Below "Pitch Black"
 * Pokémon Mega Evolution event (drops 09/12). Activates when the admin
 * picks the "Pitch Black" preset in Settings. Pure pitch-black canvas
 * with drifting neon lightning shards (pink / lime / yellow / cyan) that
 * echo the event artwork, a soft Darkrai-style wisp glow, and the
 * occasional faint electric flash.
 *
 * Discipline (matches HalloweenTheme):
 *   • All layers pointer-events:none, z-0 — never block interaction.
 *   • No jump-scares; the app is a work tool used on-shift.
 *   • CSS-only shards (inline SVG), no image fetch for the overlay.
 *   • One-shot electric chime on activation, respects the shared
 *     `wow:audio:muted` mute flag.
 */
import { useEffect, useRef, useState } from "react";
import { useTheme } from "../ThemeContext";

const NEON = ["#EC008C", "#A3E635", "#FDE047", "#22D3EE", "#A855F7"];

// One-shot electric chime — a short bright rising blip (Web Audio API).
const playZap = () => {
  try {
    if (localStorage.getItem("wow:audio:muted") === "1") return;
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const notes = [523.25, 783.99, 1046.5];
    notes.forEach((freq, i) => {
      const t = ctx.currentTime + i * 0.07;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, t);
      gain.gain.linearRampToValueAtTime(0.05, t + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.28);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.32);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 1000);
  } catch { /* audio unsupported */ }
};

// A single neon lightning shard — a jagged polyline with a glow.
const Bolt = ({ color, style }) => (
  <svg
    width="60" height="120" viewBox="0 0 60 120"
    className="absolute pointer-events-none select-none"
    style={style}
    aria-hidden="true"
  >
    <polyline
      points="34,0 14,52 30,52 20,120 50,46 32,46 44,0"
      fill={color}
      style={{ filter: `drop-shadow(0 0 6px ${color})` }}
    />
  </svg>
);

// Fixed shard layout — scattered around the edges so the centre stays
// readable. Each carries its own drift timing so they don't move in sync.
const SHARDS = [
  { top: "8%",  left: "4%",   scale: 0.9, rot: -18, dur: 9,  delay: 0,   op: 0.5 },
  { top: "22%", left: "90%",  scale: 1.1, rot: 22,  dur: 11, delay: 1.5, op: 0.45 },
  { top: "60%", left: "2%",   scale: 0.7, rot: 12,  dur: 8,  delay: 0.8, op: 0.4 },
  { top: "72%", left: "92%",  scale: 1.0, rot: -14, dur: 12, delay: 2.2, op: 0.5 },
  { top: "44%", left: "84%",  scale: 0.6, rot: 34,  dur: 10, delay: 0.4, op: 0.35 },
  { top: "88%", left: "40%",  scale: 0.8, rot: -8,  dur: 13, delay: 1.1, op: 0.3 },
  { top: "4%",  left: "60%",  scale: 0.65,rot: 16,  dur: 10, delay: 2.6, op: 0.3 },
];

export default function PitchBlackTheme({ active }) {
  const [flash, setFlash] = useState(0);
  const playedThisSession = useRef(false);

  useEffect(() => {
    if (!active || playedThisSession.current) return;
    playedThisSession.current = true;
    const t = setTimeout(() => playZap(), 300);
    return () => clearTimeout(t);
  }, [active]);

  // Faint electric flash every ~16s (skipped while tab is hidden).
  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState === "visible") setFlash((n) => n + 1);
    };
    const first = setTimeout(tick, 7000);
    const interval = setInterval(tick, 16000);
    return () => { cancelled = true; clearTimeout(first); clearInterval(interval); };
  }, [active]);

  if (!active) return null;

  return (
    <>
      <style>{`
        @keyframes pb-bolt-drift {
          0%, 100% { transform: translateY(0) rotate(var(--pb-rot)) scale(var(--pb-scale)); opacity: var(--pb-op); }
          50%      { transform: translateY(-10px) rotate(var(--pb-rot)) scale(var(--pb-scale)); opacity: calc(var(--pb-op) * 0.55); }
        }
        @keyframes pb-flash {
          0%   { opacity: 0; }
          8%   { opacity: 0.16; }
          16%  { opacity: 0; }
          24%  { opacity: 0.10; }
          40%  { opacity: 0; }
          100% { opacity: 0; }
        }
      `}</style>
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden" data-testid="pitch-black-theme-overlay">
        {/* Pitch-black vignette so edges read darker than the card floor */}
        <div
          className="absolute inset-0"
          style={{ background: "radial-gradient(ellipse at 50% 30%, rgba(0,0,0,0) 40%, rgba(0,0,0,0.65) 100%)" }}
        />
        {/* Darkrai-style wisp glow — magenta/purple bloom drifting top-left */}
        <div
          className="absolute -top-24 -left-24 w-[520px] h-[520px]"
          style={{
            background: "radial-gradient(circle at center, rgba(236,0,140,0.20) 0%, rgba(168,85,247,0.10) 40%, transparent 70%)",
            filter: "blur(8px)",
          }}
        />
        {/* Lime bloom bottom-right for the neon splash balance */}
        <div
          className="absolute -bottom-24 -right-24 w-[460px] h-[460px]"
          style={{
            background: "radial-gradient(circle at center, rgba(163,230,53,0.16) 0%, rgba(34,211,238,0.08) 45%, transparent 72%)",
            filter: "blur(8px)",
          }}
        />
        {/* Neon lightning shards */}
        {SHARDS.map((s, i) => (
          <Bolt
            key={i}
            color={NEON[i % NEON.length]}
            style={{
              top: s.top,
              left: s.left,
              "--pb-rot": `${s.rot}deg`,
              "--pb-scale": s.scale,
              "--pb-op": s.op,
              animation: `pb-bolt-drift ${s.dur}s ease-in-out ${s.delay}s infinite`,
            }}
          />
        ))}
        {/* Electric flash sweep */}
        {flash > 0 && (
          <div
            key={`flash-${flash}`}
            className="absolute inset-0"
            style={{
              background: "linear-gradient(120deg, rgba(236,0,140,0.35), rgba(163,230,53,0.28), rgba(34,211,238,0.3))",
              mixBlendMode: "screen",
              animation: "pb-flash 1.1s ease-out forwards",
            }}
          />
        )}
      </div>
    </>
  );
}
